/**
 * «Личный кабинет»: как клиенты работают со страницами `my.uni-renter.ru` — три окна:
 * «Выбрать время записи» и «Изменить время записи» (`/z/<хэш>`), «Управление
 * бронированием» (`/d/<хэш>`). В каждом — сколько человек зашло, что сделали, нажали
 * ли на бота MAX/Telegram и запустили ли его. Строка — день, неделя или месяц ДЕЙСТВИЯ
 * (когорта по выдаче ссылки — «Рассылки» → «Страница брони»). Ниже — лента действий.
 * Спека `docs/specs/2026-10-03-cabinet-design.md`; числа — как у `prokat-runner cabinet-report`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import {
  type CabinetCounts,
  type CabinetEvent,
  type CabinetGroup,
  type CabinetRow,
  type CabinetWindow,
  loadCabinet,
} from "@/lib/cabinet";
import { iso, plusDays } from "@/lib/dates";

const GROUP: Record<CabinetGroup, { label: string; head: string }> = {
  day: { label: "по дням", head: "День" },
  week: { label: "по неделям", head: "Неделя" },
  month: { label: "по месяцам", head: "Месяц" },
};
const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

type Col = { key: keyof CabinetCounts; head: string; hint: string; share?: boolean; tone?: string; sep?: boolean };
const PEOPLE: Col = { key: "people", head: "Зашли", hint: "человек (ссылок), открывших окно хотя бы раз" };
const OPENS: Col = { key: "opens", head: "Открытий", hint: "всего открытий окна", sep: true };
const CLICK_MAX: Col = { key: "click_max", head: "Нажали MAX", hint: "нажали кнопку «Бот в MAX»", share: true };
const CLICK_TG: Col = { key: "click_telegram", head: "Нажали Telegram", hint: "нажали кнопку «Бот в Telegram»", share: true };
const BOT_MAX: Col = { key: "bot_max", head: "Запустили MAX", hint: "запустили бота MAX с хэшем ссылки", share: true, tone: "text-ok" };
const BOT_TG: Col = { key: "bot_telegram", head: "Запустили Telegram", hint: "запустили @unirenterbot с хэшем ссылки", share: true, tone: "text-ok" };
const CLICK_WA: Col = { key: "click_whatsapp", head: "Нажали WhatsApp", hint: "нажали WhatsApp: написать нам", share: true, sep: true };
const BOTS: Col[] = [CLICK_MAX, CLICK_TG, CLICK_WA, BOT_MAX, BOT_TG];

const WINDOWS: { id: CabinetWindow; title: string; note: string; cols: Col[] }[] = [
  {
    id: "pick",
    title: "Выбрать время записи",
    note: "Клиент ещё не записан: выбирает город, день и час. Призыв запустить бота — сразу после первой записи.",
    cols: [
      PEOPLE,
      OPENS,
      { key: "booked", head: "Записались", hint: "сами выбрали время и записались", share: true, tone: "text-ok", sep: true },
      ...BOTS,
    ],
  },
  {
    id: "change",
    title: "Изменить время записи",
    note: "Запись уже есть (своя или от сотрудника): клиент смотрит её и переносит. Боты — после переноса и у записанных сотрудником.",
    cols: [
      PEOPLE,
      OPENS,
      { key: "moved", head: "Перенесли", hint: "выбрали другое время", share: true, tone: "text-warn", sep: true },
      ...BOTS,
    ],
  },
  {
    id: "deal",
    title: "Управление бронированием",
    note: "Страница брони: даты, товары, оплата, выдача. WhatsApp — чат с нами; push и почта — подписки на уведомления.",
    cols: [
      PEOPLE,
      OPENS,
      CLICK_MAX,
      CLICK_TG,
      CLICK_WA,
      BOT_MAX,
      { ...BOT_TG, sep: true },
      { key: "push", head: "Push", hint: "подписались на push-уведомления", share: true, tone: "text-ok" },
      { key: "push_denied", head: "Отказ push", hint: "браузер отказал в push", share: true, tone: "text-err" },
      { key: "email", head: "E-mail", hint: "оставили адрес почты", share: true, tone: "text-ok" },
    ],
  },
];
const WINDOW_NAME = Object.fromEntries(WINDOWS.map((w) => [w.id, w.title])) as Record<CabinetWindow, string>;

const CHANNEL: Record<string, string> = { max: "MAX", telegram: "Telegram", whatsapp: "WhatsApp" };
const KIND: Record<CabinetEvent["kind"], { label: string; tone: string }> = {
  open: { label: "открыл страницу", tone: "text-fg-muted" },
  book: { label: "записался", tone: "text-ok" },
  move: { label: "перенёс запись", tone: "text-warn" },
  click: { label: "нажал на", tone: "text-fg" },
  bot: { label: "запустил бота", tone: "text-ok" },
  push: { label: "подписался на push", tone: "text-ok" },
  push_denied: { label: "отказ в push", tone: "text-err" },
  email: { label: "оставил почту", tone: "text-ok" },
};

function pct(a: number, b: number): string {
  return b ? `${Math.round((a * 100) / b)}%` : "";
}

function dm(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

function title(d: { day: string; to: string }, group: CabinetGroup): string {
  if (group === "month") {
    const x = new Date(`${d.day}T12:00:00`);
    const name = `${x.toLocaleDateString("ru-RU", { month: "long" })} ${d.day.slice(0, 4)}`;
    const last = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
    return d.day.slice(8) === "01" && Number(d.to.slice(8)) === last ? name : `${name} · ${dm(d.day)} – ${dm(d.to)}`;
  }
  return d.day === d.to
    ? `${dm(d.day)} ${WEEKDAY[new Date(`${d.day}T12:00:00`).getDay()]}`
    : `${dm(d.day)} – ${dm(d.to)}`;
}

function Cells({ c, cols }: { c: CabinetCounts; cols: Col[] }) {
  return (
    <>
      {cols.map((col) => {
        const v = c[col.key];
        return (
          <td key={col.key} className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${col.sep ? "border-r border-border" : ""}`}>
            <span className={`text-[14px] font-semibold ${v ? (col.tone ?? "text-fg") : "text-fg-faint"}`}>{v}</span>
            {col.share && v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, c.people)}</span>}
          </td>
        );
      })}
    </>
  );
}

function WindowTable({
  w,
  data,
  group,
  period,
}: {
  w: (typeof WINDOWS)[number];
  data: { total: CabinetCounts; rows: CabinetRow[] };
  group: CabinetGroup;
  period: { day: string; to: string };
}) {
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  // Пустые периоды не показываем: окна живут с октября 2026, нули только шумят.
  const rows = data.rows.filter((d) => w.cols.some((c) => d[c.key] > 0));
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`окно ${w.title}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
        <h2 className="text-[14px] font-semibold">{w.title}</h2>
        <span className="text-[12px] text-fg-muted">{w.note}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-auto text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={`${th} pl-4`}>{GROUP[group].head}</th>
              {w.cols.map((c) => (
                <th key={c.key} className={`${th} ${c.sep ? "border-r border-border" : ""}`} title={c.hint}>
                  {c.head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.day} className="border-t border-border hover:bg-bg-hover" data-annot={`${w.title} ${d.day}`}>
                <td className="whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums">{title(d, group)}</td>
                <Cells c={d} cols={w.cols} />
              </tr>
            ))}
            {rows.length === 0 && (
              <tr className="border-t border-border">
                <td className="py-2 pl-4 pr-3 text-fg-faint" colSpan={w.cols.length + 1}>
                  За период в это окно никто не заходил
                </td>
              </tr>
            )}
            <tr className="border-t-2 border-border font-medium" data-annot={`${w.title} итого`}>
              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-fg-muted">Итого {title(period, "week")}</td>
              <Cells c={data.total} cols={w.cols} />
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Tile({ label, value, sub }: { label: string; value: number; sub: string }) {
  return (
    <div className="min-w-[150px] rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-2.5">
      <div className="text-[11.5px] text-fg-faint">{label}</div>
      <div className="text-[20px] font-semibold tabular-nums">{value}</div>
      <div className="text-[11.5px] text-fg-muted">{sub}</div>
    </div>
  );
}

export function CabinetPage() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -29));
  const to = params.get("to") || iso(today);
  const group: CabinetGroup =
    params.get("group") === "week" || params.get("group") === "month" ? (params.get("group") as CabinetGroup) : "day";
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["cabinet", from, to, group], queryFn: () => loadCabinet(from, to, group) });
  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
      on ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
    }`;
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const td = "whitespace-nowrap px-3 py-1.5";
  const r = q.data;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка личного кабинета">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Личный кабинет</h1>
        <DateRangePicker label="Действия" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
        <div className="flex gap-1" role="group" aria-label="Группировка">
          {(Object.keys(GROUP) as CabinetGroup[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => set({ group: k === "day" ? "" : k })}
              aria-pressed={group === k}
              className={seg(group === k)}
            >
              {GROUP[k].label}
            </button>
          ))}
        </div>
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Считаю действия…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(q.error.message)}</div>}
      {r && (
        <>
          <section className="flex flex-wrap gap-3" data-annot="итоги личного кабинета">
            <Tile label="Зашли за период" value={r.total.people} sub={`человек · открытий ${r.total.opens}`} />
            <Tile
              label="Записались и перенесли"
              value={r.total.booked + r.total.moved}
              sub={`записались ${r.total.booked} · перенесли ${r.total.moved}`}
            />
            <Tile
              label="Нажали на бота"
              value={r.total.click_max + r.total.click_telegram}
              sub={`MAX ${r.total.click_max} · Telegram ${r.total.click_telegram}`}
            />
            <Tile
              label="Запустили бота"
              value={r.total.bot_max + r.total.bot_telegram}
              sub={`MAX ${r.total.bot_max} · Telegram ${r.total.bot_telegram}`}
            />
            <Tile
              label="Запись: за всё время"
              value={r.alltime.zapis.opened}
              sub={`зашли из ${r.alltime.zapis.issued} ссылок · открытий ${r.alltime.zapis.opens} · записались ${r.alltime.zapis.booked}`}
            />
            <Tile
              label="Бронь: за всё время"
              value={r.alltime.deal.opened}
              sub={`зашли из ${r.alltime.deal.issued} ссылок · открытий ${r.alltime.deal.opens}`}
            />
          </section>
          {WINDOWS.map((w) => (
            <WindowTable key={w.id} w={w} data={r.windows[w.id]} group={group} period={{ day: r.from, to: r.to }} />
          ))}
          <div className="text-[11.5px] text-fg-faint">
            Человек — одна ссылка: чат для записи, сделка для брони; в каждом столбце считается один раз, доли — от
            зашедших. Строка — день действия по Москве. Журнал открытий и нажатий на ботов в окнах записи ведётся с
            03.10.26: до этого по каждой ссылке известны только первое и последнее открытие, полный счёт — в плитках «за
            всё время».
          </div>
          <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="лента действий">
            <div className="border-b border-border px-4 py-2.5 text-[14px] font-semibold">Последние действия</div>
            <div className="overflow-x-auto">
              <table className="w-auto text-[12.5px]">
                <thead className="text-left text-[11.5px] text-fg-faint">
                  <tr>
                    <th className={`${th} pl-4`}>Когда</th>
                    <th className={th}>Окно</th>
                    <th className={th}>Действие</th>
                    <th className={th}>Чат</th>
                    <th className={th}>Сделка / лид</th>
                  </tr>
                </thead>
                <tbody>
                  {r.feed.map((e, i) => (
                    <tr key={`${e.at}-${i}`} className="border-t border-border hover:bg-bg-hover">
                      <td className={`${td} pl-4 tabular-nums`}>
                        {dm(e.at)} {e.at.slice(11, 16)}
                      </td>
                      <td className={`${td} text-fg-muted`}>{WINDOW_NAME[e.window]}</td>
                      <td className={`${td} ${KIND[e.kind]?.tone ?? ""}`}>
                        {KIND[e.kind]?.label ?? e.kind}
                        {e.channel ? ` ${CHANNEL[e.channel] ?? e.channel}` : ""}
                      </td>
                      <td className={td}>
                        {e.chat_id ? (
                          <Link to={`/chats/${e.chat_id}`} className="text-accent hover:underline">
                            {e.chat_id}
                          </Link>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                      </td>
                      <td className={td}>
                        {e.deal_id ? (
                          <Link to={`/deals/${e.deal_id}`} className="text-accent hover:underline">
                            {e.deal_id}
                          </Link>
                        ) : e.lead_id ? (
                          <Link to={`/leads/${e.lead_id}`} className="text-accent hover:underline" title="лид: сделки ещё нет">
                            лид {e.lead_id}
                          </Link>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {r.feed.length === 0 && (
                    <tr className="border-t border-border">
                      <td className="py-2 pl-4 pr-3 text-fg-faint" colSpan={5}>
                        За период действий нет
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
